import { useEffect, useState } from "react";
import { getRelGeral } from "@/services/report";
import { getAllEspecificas } from "@/services/especifica";
import { getAllServicos } from "@/services/servico";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Select, Button, Input } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { mascaraCpf, mascaraMatricula } from "@/utils/masks";
import "@/styles/globals.css";

interface Especifica {
  id: number;
  nome: string;
}

interface Servico {
  id: number;
  nome: string;
}

interface Unidade {
  id: number;
  nome: string;
}

interface Turma {
  id: number;
  nome: string;
}

// O filtro de Disciplina lista específicas e serviços na mesma caixa; o prefixo
// diz ao back-end qual dos dois filtrar ("E-12" = específica 12, "S-3" = serviço 3).
const PREFIXO_ESPECIFICA = "E-";
const PREFIXO_SERVICO = "S-";

interface RelGeralItem {
  nomeUnidade: string;
  matricula: number;
  nomeAluno: string;
  nomeResponsavel: string;
  cpfResponsavel: string;
  nomePagamento: string;
  nomeTurma: string;
  nomeEspecifica: string;
  quantidadeParcelas: number;
  dataPrimeiraParcela: string;
  valorEspecifica: number;
}

interface TotalRow {
  nomeUnidade: string;
  matricula: null;
  nomeAluno: string;
  nomeResponsavel: string;
  cpfResponsavel: string;
  nomePagamento: string;
  nomeTurma: string;
  nomeEspecifica: string;
  quantidadeParcelas: null;
  dataPrimeiraParcela: string;
  valorEspecifica: number;
}

type TableDataType = RelGeralItem | TotalRow;

export default function RelGeral() {
  const [especificas, setEspecificas] = useState<Especifica[]>([]);
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [filtroDisciplina, setFiltroDisciplina] = useState("");
  const [filtroUnidade, setFiltroUnidade] = useState<number | "">("");
  const [filtroTurma, setFiltroTurma] = useState<number | "">("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<RelGeralItem[]>([]);

  useEffect(() => {
    getAllEspecificas().then((res) => setEspecificas(res.data || []));
    getAllServicos().then((res) => setServicos(res.data || []));
    getUnidades().then((res) => setUnidades(res.data || []));
    getTurma().then((res) => setTurmas(res.data || []));
  }, []);

  const buscar = async () => {
    // Validação das datas
    if (!dataInicio || !dataFim) {
      alert("Preencha Data Início e Data Fim.");
      return;
    }
    if (new Date(dataInicio) > new Date(dataFim)) {
      alert("Data Início deve ser menor que Data Fim.");
      return;
    }
    setLoading(true);
    const params = {
      dataInicial: dataInicio,
      dataFinal: dataFim,
      especificaId: filtroDisciplina.startsWith(PREFIXO_ESPECIFICA)
        ? filtroDisciplina.replace(PREFIXO_ESPECIFICA, "")
        : undefined,
      servicoId: filtroDisciplina.startsWith(PREFIXO_SERVICO)
        ? filtroDisciplina.replace(PREFIXO_SERVICO, "")
        : undefined,
      unidadeId: filtroUnidade || undefined,
      turmaId: filtroTurma || undefined,
    };
    const res = await getRelGeral(params);
    if (res && res.data) {
      setDados(res.data);
    } else {
      setDados([]);
    }
    setLoading(false);
  };

  const exportToExcel = () => {
    const exportData = dados.map(item => ({
      "Unidade": item.nomeUnidade,
      "Matrícula": mascaraMatricula(item.matricula.toString()),
      "Aluno": item.nomeAluno,
      "Responsável": item.nomeResponsavel,
      "CPF Resp.": item.cpfResponsavel ? mascaraCpf(item.cpfResponsavel) : "",
      "Pagamento": item.nomePagamento,
      "Turma": item.nomeTurma,
      "Específica": item.nomeEspecifica,
      "Data 1ª Parcela": item.dataPrimeiraParcela ? format(new Date(item.dataPrimeiraParcela), "dd/MM/yyyy") : "",
      "Quantidade Parcelas": item.quantidadeParcelas,
      "Valor Específica": item.valorEspecifica, // Manter como número para formatação
    }));
    
    // Adiciona a linha de total
    exportData.push({
      "Unidade": "",
      "Matrícula": "",
      "Aluno": "",
      "Responsável": "",
      "CPF Resp.": "",
      "Pagamento": "",
      "Turma": "",
      "Específica": "Total",
      "Data 1ª Parcela": "",
      "Quantidade Parcelas": 0,
      "Valor Específica": valorTotal,
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Aplicar formatação de alinhamento às colunas numéricas
    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Aplicar alinhamento à direita para as colunas numéricas
      for (let row = range.s.r; row <= range.e.r; row++) {
        // Coluna H (Quantidade Parcelas) - índice 7
        const cellAddressQtd = XLSX.utils.encode_cell({ r: row, c: 7 });
        if (ws[cellAddressQtd]) {
          if (!ws[cellAddressQtd].s) ws[cellAddressQtd].s = {};
          ws[cellAddressQtd].s.alignment = { horizontal: 'right' };
        }
        
        // Coluna I (Valor Específica) - índice 8
        const cellAddressValor = XLSX.utils.encode_cell({ r: row, c: 8 });
        if (ws[cellAddressValor]) {
          if (!ws[cellAddressValor].s) ws[cellAddressValor].s = {};
          ws[cellAddressValor].s.alignment = { horizontal: 'right' };
          // Aplicar formato de moeda brasileira
          ws[cellAddressValor].z = 'R$ #,##0.00';
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelatorioGeral");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "relatorio_geral.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("Relatório Geral", 14, 14);

    const head = [
      "Unidade", "Matrícula", "Aluno", "Responsável", "CPF Resp.", "Pagamento",
      "Turma", "Específica", "Data 1ª Parcela", "Qtd Parcelas", "Valor Específica"
    ];

    const tableData = dados.map(item => [
      item.nomeUnidade || "",
      mascaraMatricula(item.matricula.toString()),
      item.nomeAluno || "",
      item.nomeResponsavel || "",
      item.cpfResponsavel ? mascaraCpf(item.cpfResponsavel) : "",
      item.nomePagamento || "",
      item.nomeTurma || "",
      item.nomeEspecifica || "",
      item.dataPrimeiraParcela ? format(new Date(item.dataPrimeiraParcela), "dd/MM/yyyy") : "",
      item.quantidadeParcelas || 0,
      currencyFormatter.format(item.valorEspecifica || 0),
    ]);
    // Adiciona a linha de total
    tableData.push([
      "","","", "", "", "", "", "Total", "", "", currencyFormatter.format(valorTotal)
    ]);

    autoTable(doc, {
      head: [head],
      body: tableData,
      startY: 24,
      styles: { fontSize: 9 },
      margin: { left: 10, right: 10 },
      tableWidth: 'auto',
      columnStyles: {
        7: { halign: 'right' }, // Qtd Parcelas
        8: { halign: 'right' }, // Valor Específica
      },
      didParseCell: function (data) {
        // Deixa a linha de total em negrito e maior
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
        }
      }
    });

    doc.save("relatorio_geral.pdf");
  };

  // Os dados já vêm "flatten" do back-end (uma linha por específica)
  const dadosTable = dados;

  const valorTotal = dadosTable.reduce((acc, item) => acc + item.valorEspecifica, 0);

  const totalRow: TotalRow = {
    nomeUnidade: "",
    matricula: null,
    nomeAluno: "",
    nomeResponsavel: "",
    cpfResponsavel: "",
    nomePagamento: "",
    nomeTurma: "",
    nomeEspecifica: "Total",
    quantidadeParcelas: null,
    dataPrimeiraParcela: "",
    valorEspecifica: valorTotal,
  };

  const dadosTableComTotal: TableDataType[] = [...dadosTable, totalRow];

  const columns: ColumnsType<TableDataType> = [
    { title: "Unidade", dataIndex: "nomeUnidade", key: "nomeUnidade" },
    { 
      title: "Matrícula", 
      dataIndex: "matricula", 
      key: "matricula",
      render: (matricula: number | null) => matricula ? mascaraMatricula(matricula.toString()) : ""
    },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Responsável", dataIndex: "nomeResponsavel", key: "nomeResponsavel" },
    { title: "CPF Resp.", dataIndex: "cpfResponsavel", key: "cpfResponsavel", render: (cpf: string) => cpf ? mascaraCpf(cpf) : "" },
    { title: "Pagamento", dataIndex: "nomePagamento", key: "nomePagamento" },
    { title: "Turma", dataIndex: "nomeTurma", key: "nomeTurma" },
    { title: "Específica", dataIndex: "nomeEspecifica", key: "nomeEspecifica" },
    {
      title: "Data 1ª Parcela",
      dataIndex: "dataPrimeiraParcela",
      key: "dataPrimeiraParcela",
      render: (date: string) => (date ? format(new Date(date), "dd/MM/yyyy") : ""),
    },
    { 
      title: "Qtd Parcelas", 
      dataIndex: "quantidadeParcelas", 
      key: "quantidadeParcelas",
      align: "right",
      className: "text-right"
    },
    {
      title: "Valor Específica",
      dataIndex: "valorEspecifica",
      key: "valorEspecifica",
      align: "right",
      className: "text-right",
      render: (v: number) => currencyFormatter.format(v),
    },
  ];

  const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Relatório Geral</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <Select
            value={filtroUnidade || undefined}
            onChange={(val) => setFiltroUnidade(val ?? "")}
            style={{ width: 200 }}
            allowClear
            placeholder="Unidade"
            options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
          />
          <Select
            value={filtroTurma || undefined}
            onChange={(val) => setFiltroTurma(val ?? "")}
            style={{ width: 200 }}
            allowClear
            placeholder="Turma"
            options={turmas.map((t) => ({ value: t.id, label: t.nome }))}
          />
          <Select
            value={filtroDisciplina}
            onChange={(val) => setFiltroDisciplina(val ?? "")}
            style={{ width: 240 }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Disciplina"
            options={[
              { value: "", label: "Todas" },
              {
                label: "Específicas",
                options: especificas.map((e) => ({ value: `${PREFIXO_ESPECIFICA}${e.id}`, label: e.nome })),
              },
              {
                label: "Serviços",
                options: servicos.map((s) => ({ value: `${PREFIXO_SERVICO}${s.id}`, label: s.nome })),
              },
            ]}
          />
          <Input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            style={{ width: 160 }}
            placeholder="Data Início"
          />
          <Input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            style={{ width: 160 }}
            placeholder="Data Fim"
          />
          <Button type="primary" loading={loading} onClick={buscar}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>Exportar Excel</Button>
          <Button onClick={exportToPDF} disabled={dados.length === 0}>Exportar PDF</Button>
        </div>
        <Table
          columns={columns}
          dataSource={dadosTableComTotal}
          rowKey={(row, idx) => row.matricula ? `${row.matricula}-${row.nomeEspecifica}` : `total-row-${idx}`}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
          rowClassName={(_, idx) => idx === dadosTableComTotal.length - 1 ? 'tabela-total-row' : ''}
        />
      </ConfigProvider>
    </>
  );
} 